import React, { useEffect, useState } from 'react';
import { Phone, Clock } from 'lucide-react';

// Business hours window (visitor's local time): Monday–Friday 08:00–18:00.
// Anything outside this — including weekends — is "out-of-hours".
// Lutrom assets are UK-based, so the visitor's device clock matches the
// engineering team's working day in 99% of scans.
const BUSINESS_DAYS = [1, 2, 3, 4, 5];  // Mon–Fri (Sun=0, Mon=1, ... Sat=6)
const BUSINESS_HOUR_START = 8;
const BUSINESS_HOUR_END = 18;

const isOutOfHours = (now = new Date()) => {
  const day = now.getDay();
  const hour = now.getHours();
  if (!BUSINESS_DAYS.includes(day)) return true;
  return hour < BUSINESS_HOUR_START || hour >= BUSINESS_HOUR_END;
};

/**
 * Sticky out-of-hours emergency CTA shown on customer-facing QR landings.
 *
 * Renders only outside Lutrom business hours so the in-hours UX stays clean
 * (visitors who scan during the day go straight to the request form). When
 * shown, it's deliberately the most prominent thing on the page — a red
 * "tap to call" pill that turns every Lutrom-tagged asset into a 24/7 service
 * entry-point with a single tap.
 */
const OutOfHoursBanner = ({ phoneNumber = '+44 7599 474730' }) => {
  // Re-evaluate every minute so a banner that mounts at 17:59 hides at 18:00
  // (in either direction) without a hard refresh.
  const [show, setShow] = useState(() => isOutOfHours());
  useEffect(() => {
    const id = setInterval(() => setShow(isOutOfHours()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!show) return null;

  const telHref = `tel:${phoneNumber.replace(/\s/g, '')}`;
  return (
    <a
      href={telHref}
      data-testid="out-of-hours-banner"
      className="block w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white shadow-lg active:shadow-sm transition-shadow"
    >
      <div className="max-w-2xl mx-auto px-5 py-4 sm:py-5 flex items-center gap-4">
        <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center shrink-0 ring-1 ring-white/30">
          <Phone className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-medium text-red-100">
            <Clock className="h-3 w-3" /> Out-of-hours · 24/7 cover
          </div>
          <div className="font-display text-base sm:text-lg leading-tight mt-0.5 truncate">
            Tap to call Lutrom Engineering
          </div>
          <div className="text-xs text-red-50/90 font-mono tracking-wide truncate" data-testid="out-of-hours-phone">
            {phoneNumber}
          </div>
        </div>
        <div className="hidden sm:flex shrink-0 items-center gap-2 px-3 py-2 rounded-md bg-white text-red-700 text-xs font-semibold uppercase tracking-wider">
          <Phone className="h-3.5 w-3.5" /> Call now
        </div>
      </div>
    </a>
  );
};

export default OutOfHoursBanner;
export { isOutOfHours };
